import { memoryStore, useStore } from "../../../lib/store";
import { revalidateApiQuery, type Api } from "../../../services/api";

/**
 * The rubric, inferred from the endpoint that stores it rather than redeclared.
 * A second shape that "mostly matches" the contract is how the two drift, and
 * this one is a rubric a medical team validated word for word.
 */
export type AnnotationVerdict =
  Api["post"]["/webapp/private/conversations/:id/annotations"]["input"]["json"]["verdict"];

/**
 * Which answer is being judged, resolved by the caller because only the page
 * knows it: the chat's action bar has the turn index but no conversation id (its
 * `viewStateKey` is an article id on Chat-with-Paper), and the question/answer
 * text is what the sheet shows above the form.
 *
 * `existing` prefills a revision. A verdict is revisable, so reopening must show
 * what was said rather than a blank form — and it comes from the conversation
 * read's `myAnnotations`, which the page already has.
 */
export type AnnotationTarget = {
  subject: AnnotationSubject;
  question: string;
  answer: string;
  existing: AnnotationVerdict | null;
};

// The judged answer: a legacy turn by its index, an assistant answer by its run.
export type AnnotationSubject =
  | { engine: "legacy"; conversationId: string; turnIndex: number }
  | { engine: "assistant"; conversationId: string; runId: string };

// One of the annotator's verdicts on a subject; both engines return this shape.
export type SubjectAnnotation = Omit<
  Api["get"]["/assistant/conversations/:conversationId/annotations"]["output"]["body"]["annotations"][number],
  "runId"
>;

// Refreshes the read that lists the annotator's verdicts on the subject.
export async function revalidateSubjectAnnotations(
  subject: AnnotationSubject
): Promise<void> {
  switch (subject.engine) {
    case "legacy":
      await revalidateApiQuery("/webapp/private/conversations/:id", {
        param: { id: subject.conversationId },
      });
      return;
    case "assistant":
      await revalidateApiQuery(
        "/assistant/conversations/:conversationId/annotations",
        { param: { conversationId: subject.conversationId } }
      );
      return;
    default:
      throw new Error(subject satisfies never);
  }
}

/**
 * In-sheet navigation. A discriminated union rather than a bare string so the
 * renderer stays exhaustively checked.
 *
 * `skip` is its own view because passing is not a one-tap exit: an expert who
 * cannot judge a turn has to say WHY, and the reason is a closed two-value set.
 */
export type AnnotationView =
  { view: "rate" } | { view: "skip" } | { view: "compare" };

type AnnotationSheetState = {
  open: boolean;
  target: AnnotationTarget | null;
  stack: readonly AnnotationView[];
};

const CLOSED: AnnotationSheetState = {
  open: false,
  target: null,
  stack: [{ view: "rate" }],
};

// Module-level singleton: the sheet is mounted once, app-wide, and opened
// imperatively from a chat action bar — the same shape as the settings sheet and
// `displayToast`. Copied rather than imported: `settingsSheetStore` is a
// singleton whose union enumerates settings views, and nothing in it is
// parameterised.
const store = memoryStore<AnnotationSheetState>(CLOSED);

export function useAnnotationSheet(): AnnotationSheetState {
  const [state] = useStore(store);
  return state;
}

export function openAnnotation(target: AnnotationTarget): void {
  store.set({ open: true, target, stack: [{ view: "rate" }] });
}

/**
 * The evaluation actions of a legacy conversation, for each page that shows one.
 *
 * `origin` is part of the match: the side-car carries both arms, and a match on
 * `turnIndex` alone would prefill our rubric with a competitor's verdict. The
 * comparisons are not copied into the target: the sheet reads them from the
 * conversation query, which every write revalidates.
 */
export function legacyEvaluation(
  conversationId: string,
  myAnnotations: ReadonlyArray<
    Api["get"]["/webapp/private/conversations/:id"]["output"]["body"]["myAnnotations"][number]
  >
) {
  const ours = myAnnotations.filter(
    (annotation) => annotation.origin === "juisci"
  );
  return {
    onEvaluate: (turn: {
      turnIndex: number;
      question: string;
      answer: string;
    }) => {
      const existing = ours.find(
        (annotation) => annotation.turnIndex === turn.turnIndex
      );
      openAnnotation({
        subject: {
          engine: "legacy",
          conversationId,
          turnIndex: turn.turnIndex,
        },
        question: turn.question,
        answer: turn.answer,
        // A revision opens on what was said rather than blank.
        existing: existing === undefined ? null : existing.verdict,
      });
    },
    // A turn counts as evaluated when our answer has been rated.
    evaluatedTurnIndexes: ours.map((annotation) => annotation.turnIndex),
  };
}

/**
 * Fold a just-written verdict back into the open target.
 *
 * Without this, stepping back to phase 1 from the comparison shows a BLANK form:
 * `target.existing` was captured when the sheet opened, so it still says "nothing
 * rated yet" a second after the rating was stored. Re-submitting that blank form
 * would then file a revision made of nothing — the one shape a revisable verdict
 * must never take. Revalidating the conversation query refreshes the chat's icon,
 * not this sheet's target, which is why the sheet has to be told too.
 */
export function recordAnnotationVerdict(verdict: AnnotationVerdict): void {
  store.set((prev) => {
    if (prev.target === null) return prev;
    return { ...prev, target: { ...prev.target, existing: verdict } };
  });
}

export function closeAnnotation(): void {
  // The target is kept while the sheet slides out — clearing it here would
  // unmount the form mid-animation and flash an empty panel.
  store.set((prev) => ({ ...prev, open: false }));
}

export function pushAnnotationView(view: AnnotationView): void {
  store.set((prev) => ({
    ...prev,
    open: true,
    stack: [...prev.stack, view],
  }));
}

export function popAnnotationView(): void {
  store.set((prev) => {
    if (prev.stack.length <= 1) return { ...prev, open: false };
    return { ...prev, stack: prev.stack.slice(0, -1) };
  });
}
